import React, { useState } from 'react';
import { Clock, Play } from 'lucide-react';
import { TimeRecordsFilters } from '../components/time-records/TimeRecordsFilters';
import { TimeRecordsTable } from '../components/time-records/TimeRecordsTable';
import { TimeRecordsStats } from '../components/time-records/TimeRecordsStats';
import { GenerateTimeRecordsDialog } from '../components/time-records/GenerateTimeRecordsDialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Pagination,
  Button,
} from '../components/ui';
import { LoadingTable } from '../components/ui/loading-states';
import { ErrorState } from '../components/ui/error-state';
import { useTimeRecords, useTimeRecordsStatistics, useGenerateTimeRecords } from '../hooks/useTimeRecords';
import { useEmployees } from '../hooks/useEmployees';
import { useDepartmentsForSelect } from '../hooks/useDepartments';
import { usePositions } from '../hooks/usePositions';
import { useOrganizationsForSelect } from '../hooks/organizations/useOrganizations';
import type { TimeRecordFilters } from '../types/time-records.types';

export function TimeRecords() {
  // Initialize filters with default date range (last 30 days)
  const getDefaultDateFrom = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };

  const getDefaultDateTo = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [filters, setFilters] = useState<TimeRecordFilters>({
    dateFrom: getDefaultDateFrom(),
    dateTo: getDefaultDateTo(),
    page: 1,
    limit: 20,
    sort: 'work_date:DESC',
  });

  // Organization filter state
  const [organizationFilter, setOrganizationFilter] = useState<string>('');

  // Generate dialog state
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);

  // Fetch data
  const { data, isLoading, error, refetch } = useTimeRecords(filters);

  // Generate mutation
  const generateMutation = useGenerateTimeRecords();
  const { data: employees } = useEmployees({ limit: 10000 });
  const { data: departments } = useDepartmentsForSelect(organizationFilter || null);
  const { data: organizations } = useOrganizationsForSelect();
  const { data: positions } = usePositions({ limit: 1000 });

  // Calculate statistics from fetched records
  const stats = useTimeRecordsStatistics(data?.data);

  const handleFiltersChange = (newFilters: Partial<TimeRecordFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const handleOrganizationChange = (orgId: string) => {
    setOrganizationFilter(orgId);
    setFilters((prev) => ({
      ...prev,
      page: 1,
      organization_id: orgId || undefined,
      department_id: undefined,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleGenerateDialogOpen = () => {
    generateMutation.reset();
    setIsGenerateDialogOpen(true);
  };

  const handleGenerate = (params: {
    start_date: string;
    end_date: string;
    employee_id?: string;
    overwrite?: boolean;
  }) => {
    generateMutation.mutate(params);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Учёт рабочего времени</h1>
          <p className="text-muted-foreground">
            Мониторинг и анализ отработанного времени сотрудников
          </p>
        </div>
        <ErrorState title="Ошибка загрузки записей учёта времени" onAction={refetch} />
      </div>
    );
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Учёт рабочего времени</h1>
          <p className="text-muted-foreground">
            Мониторинг и анализ отработанного времени сотрудников
          </p>
        </div>
        <Button onClick={handleGenerateDialogOpen}>
          <Play className="h-4 w-4 mr-2" />
          Генерация записей
        </Button>
      </div>

      {/* Filters */}
      <TimeRecordsFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        employees={employees}
        departments={departments}
        organizations={organizations}
        organizationFilter={organizationFilter}
        onOrganizationChange={handleOrganizationChange}
        positions={positions}
      />

      {/* Statistics */}
      <TimeRecordsStats stats={stats} isLoading={isLoading} />

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Записи учёта времени</CardTitle>
          <CardDescription>
            {data && `Показано ${data.data.length} из ${data.total} записей`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <TimeRecordsTable records={data.data} />

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-semibold text-muted-foreground">
                Записи не найдены
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Попробуйте изменить фильтры поиска или выберите другой период.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Убедитесь, что у сотрудников есть события посещаемости за выбранный период.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Dialog */}
      <GenerateTimeRecordsDialog
        open={isGenerateDialogOpen}
        onOpenChange={setIsGenerateDialogOpen}
        onGenerate={handleGenerate}
        isLoading={generateMutation.isPending}
        result={generateMutation.data ?? null}
        error={generateMutation.error}
        employees={employees}
      />
    </div>
  );
}
